// Node.js port of csharp Assembler.Timing.PhaseTiming / lean4 Assembler.Base.Timing.PhaseTiming.
//
// Measures engine work in the closed phase vocabulary (TimingPhase) so every engine reports
// comparable load and merge splits (PLAN_TIMING_FIX.md).
//
// Responsibilities:
// - Bracket one loader build, one merge, one harness step or one web request as a scope
//   (enter/leave) labelled with the lane and engine chosen by the caller that owns this value;
//   within() runs one body as such a scope
// - Charge each measured phase its exclusive (self) time: nested phases are subtracted from their
//   parent, so the phases of a scope plus its derived "other" sum exactly to the scope total
// - Hand parallel work a child value (fork) and fold it back (join)
// - Report totals in nanoseconds (snapshot) and scope/phase counts (snapshotCounts)
// - Own the process clocks: getMonotonicNanoseconds() for elapsed time and deadlines, utcNow() for
//   externally meaningful timestamps
//
// Current behavior:
// - The value is passed in explicitly (loader builds and mergeTemplates); it is never global,
//   static or ambient. One value serves one call chain at a time: a console run, or one web request.
// - When disabled, measure() only runs its body and every other member is a no-op.
// - Misuse while enabled (a phase outside a scope, a phase from the wrong stage, an unbalanced
//   scope) throws, because a silently misattributed timing is indistinguishable from a real one.
// - Keys are "lane.engine.stage.phase" plus "lane.engine.stage" (the scope total) and
//   "lane.engine.stage.other" (scope time not covered by a measured phase).
import { TimingPhase } from './timingPhase.js';
import { TimingStage } from './timingStage.js';

interface TimingFrame {
    phase: TimingPhase;
    startNanos: bigint;
    childNanos: bigint;
}

interface TimingScope {
    lane: string;
    engine: string;
    stage: TimingStage;
    startNanos: bigint;
    phaseNanos: bigint;
    nestedNanos: bigint;
    frames: TimingFrame[];
}

export class PhaseTiming {
    private readonly _enabled: boolean;
    private readonly _nanos = new Map<string, number>();
    private readonly _counts = new Map<string, number>();
    private readonly _scopes: TimingScope[] = [];
    private _forkScopeKey: string | null = null;

    constructor(enabled: boolean) {
        this._enabled = enabled;
    }

    get enabled(): boolean {
        return this._enabled;
    }

    /**
     * Monotonic nanoseconds for elapsed time and operational deadlines, independent of collection.
     */
    static getMonotonicNanoseconds(): bigint {
        return process.hrtime.bigint();
    }

    /**
     * The UTC instant for externally meaningful timestamps; never use it for elapsed time.
     */
    static utcNow(): Date {
        return new Date();
    }

    /**
     * Opens the scope for one loader build, merge, harness step or request. Different stages may
     * nest with independent phase stacks.
     */
    enter(lane: string, engine: string, stage: TimingStage): void {
        if (!this._enabled) return;
        const parent = this.currentScope();
        if (parent !== null && parent.stage === stage)
            throw new Error(`PhaseTiming cannot nest two ${PhaseTiming.stageName(stage)} scopes.`);
        this._scopes.push({ lane, engine, stage, startNanos: PhaseTiming.getMonotonicNanoseconds(), phaseNanos: 0n, nestedNanos: 0n, frames: [] });
    }

    /**
     * Closes the open scope and records its total, its derived "other" remainder and its count.
     */
    leave(): void {
        if (!this._enabled) return;
        const scope = this.currentScope();
        if (scope === null)
            throw new Error('PhaseTiming.leave called without an open scope.');
        if (scope.frames.length !== 0) {
            throw new Error(
                `PhaseTiming scope ${PhaseTiming.key(scope.lane, scope.engine, scope.stage)} closed with ${scope.frames.length} open phase(s).`);
        }
        const total = PhaseTiming.getMonotonicNanoseconds() - scope.startNanos;
        const stageKey = PhaseTiming.key(scope.lane, scope.engine, scope.stage);
        PhaseTiming.add(this._nanos, stageKey, Number(total));
        PhaseTiming.add(this._nanos, stageKey + '.other', Number(total - scope.phaseNanos - scope.nestedNanos));
        PhaseTiming.add(this._nanos, stageKey + '.nested', Number(scope.nestedNanos));
        PhaseTiming.add(this._counts, stageKey, 1);
        this._scopes.pop();
        const caller = this.currentScope();
        if (caller !== null) {
            caller.nestedNanos += total;
            PhaseTiming.add(this._counts, PhaseTiming.key(caller.lane, caller.engine, caller.stage) + '.nested', 1);
            if (caller.frames.length > 0)
                caller.frames[caller.frames.length - 1].childNanos += total;
        }
    }

    /**
     * Runs body as one enter()...leave() scope. The scope closes even when body throws.
     */
    within<T>(lane: string, engine: string, stage: TimingStage, body: () => T): T {
        this.enter(lane, engine, stage);
        try {
            return body();
        } finally {
            this.leave();
        }
    }

    /**
     * Runs an async body as one enter()...leave() scope. The scope closes even when body rejects.
     */
    async withinAsync<T>(lane: string, engine: string, stage: TimingStage, body: () => Promise<T>): Promise<T> {
        if (!this._enabled) return await body();
        this.enter(lane, engine, stage);
        try {
            return await body();
        } finally {
            this.leave();
        }
    }

    /**
     * Runs body as phase of the open scope and charges it its self time.
     */
    measure<T>(phase: TimingPhase, body: () => T): T {
        if (!this._enabled) return body();
        this.beginMeasure(phase);
        try {
            return body();
        } finally {
            this.endMeasure();
        }
    }

    /**
     * Runs an async body as phase of the open scope and charges it its self time.
     */
    async measureAsync<T>(phase: TimingPhase, body: () => Promise<T>): Promise<T> {
        if (!this._enabled) return await body();
        this.beginMeasure(phase);
        try {
            return await body();
        } finally {
            this.endMeasure();
        }
    }

    /**
     * Returns a child value for work that runs in parallel inside the open scope. The child records
     * under the same lane, engine and stage; join() folds its phases back.
     */
    fork(): PhaseTiming {
        const child = new PhaseTiming(this._enabled);
        if (!this._enabled) return child;
        const scope = this.currentScope();
        if (scope === null)
            throw new Error('PhaseTiming.fork called without an open scope.');
        child._forkScopeKey = PhaseTiming.key(scope.lane, scope.engine, scope.stage);
        child._scopes.push({
            lane: scope.lane,
            engine: scope.engine,
            stage: scope.stage,
            startNanos: PhaseTiming.getMonotonicNanoseconds(),
            phaseNanos: 0n,
            nestedNanos: 0n,
            frames: []
        });
        return child;
    }

    /**
     * Folds a forked child's phase totals into this value. Parallel children overlap in wall time, so
     * a scope that joined children is exempt from the phases-sum-to-total invariant.
     */
    join(child: PhaseTiming): void {
        if (!this._enabled) return;
        if (child._scopes.length !== 0)
            throw new Error('PhaseTiming.join called on a child with open scopes.');
        for (const [key, value] of child._nanos) {
            if (key !== child._forkScopeKey && key !== child._forkScopeKey + '.other')
                PhaseTiming.add(this._nanos, key, value);
        }
        for (const [key, value] of child._counts) {
            if (key !== child._forkScopeKey)
                PhaseTiming.add(this._counts, key, value);
        }
    }

    /**
     * Totals in nanoseconds keyed "lane.engine.stage[.phase]".
     */
    snapshot(): ReadonlyMap<string, number> {
        return new Map(this._nanos);
    }

    /**
     * Scope and phase counts keyed "lane.engine.stage[.phase]".
     */
    snapshotCounts(): ReadonlyMap<string, number> {
        return new Map(this._counts);
    }

    /**
     * Clears every total and count. The value must not have an open scope.
     */
    reset(): void {
        if (this._scopes.length !== 0)
            throw new Error('PhaseTiming.reset called with an open scope.');
        this._nanos.clear();
        this._counts.clear();
        this._forkScopeKey = null;
    }

    /**
     * The lowercase name used in keys and reports.
     */
    static stageName(stage: TimingStage): string {
        switch (stage) {
            case TimingStage.Load: return 'load';
            case TimingStage.Merge: return 'merge';
            case TimingStage.Harness: return 'harness';
            case TimingStage.Request: return 'request';
            default: throw new Error(`Unknown timing stage '${stage as number}'.`);
        }
    }

    /**
     * The lowercase name used in keys and reports.
     */
    static phaseName(phase: TimingPhase): string {
        switch (phase) {
            case TimingPhase.Source: return 'source';
            case TimingPhase.Parse: return 'parse';
            case TimingPhase.Prepare: return 'prepare';
            case TimingPhase.Dynamic: return 'dynamic';
            case TimingPhase.Materialize: return 'materialize';
            case TimingPhase.Resolve: return 'resolve';
            case TimingPhase.Bind: return 'bind';
            case TimingPhase.Render: return 'render';
            case TimingPhase.Assemble: return 'assemble';
            case TimingPhase.Initialize: return 'initialize';
            case TimingPhase.Execute: return 'execute';
            case TimingPhase.Compare: return 'compare';
            case TimingPhase.Save: return 'save';
            case TimingPhase.Commit: return 'commit';
            case TimingPhase.Summary: return 'summary';
            case TimingPhase.Report: return 'report';
            case TimingPhase.Generate: return 'generate';
            case TimingPhase.Package: return 'package';
            case TimingPhase.Boot: return 'boot';
            case TimingPhase.Hydrate: return 'hydrate';
            case TimingPhase.Replay: return 'replay';
            case TimingPhase.Serialize: return 'serialize';
            default: throw new Error(`Unknown timing phase '${phase as number}'.`);
        }
    }

    /**
     * Whether phase may be measured inside a stage scope.
     */
    static belongsTo(phase: TimingPhase, stage: TimingStage): boolean {
        switch (stage) {
            case TimingStage.Load:
                return phase === TimingPhase.Source || phase === TimingPhase.Parse || phase === TimingPhase.Prepare
                    || phase === TimingPhase.Dynamic || phase === TimingPhase.Materialize;
            case TimingStage.Merge:
                return phase === TimingPhase.Resolve || phase === TimingPhase.Bind || phase === TimingPhase.Dynamic
                    || phase === TimingPhase.Render || phase === TimingPhase.Assemble;
            case TimingStage.Harness:
                return phase === TimingPhase.Initialize || phase === TimingPhase.Execute || phase === TimingPhase.Compare
                    || phase === TimingPhase.Save || phase === TimingPhase.Commit || phase === TimingPhase.Summary
                    || phase === TimingPhase.Report || phase === TimingPhase.Generate || phase === TimingPhase.Package
                    || phase === TimingPhase.Boot || phase === TimingPhase.Hydrate || phase === TimingPhase.Replay
                    || phase === TimingPhase.Serialize;
            case TimingStage.Request:
                return phase === TimingPhase.Resolve || phase === TimingPhase.Dynamic || phase === TimingPhase.Serialize;
            default: throw new Error(`Unknown timing stage '${stage as number}'.`);
        }
    }

    /**
     * Opens a phase frame. Close it with endMeasure() in a finally block.
     */
    beginMeasure(phase: TimingPhase): void {
        if (!this._enabled) return;
        const scope = this.currentScope();
        if (scope === null)
            throw new Error(`PhaseTiming phase ${PhaseTiming.phaseName(phase)} measured without an open scope.`);
        if (!PhaseTiming.belongsTo(phase, scope.stage)) {
            throw new Error(
                `PhaseTiming phase ${PhaseTiming.phaseName(phase)} does not belong to stage ${PhaseTiming.stageName(scope.stage)}.`);
        }
        scope.frames.push({ phase, startNanos: PhaseTiming.getMonotonicNanoseconds(), childNanos: 0n });
    }

    /**
     * Closes the active phase frame and records its self time.
     */
    endMeasure(): void {
        if (!this._enabled) return;
        const scope = this.currentScope();
        if (scope === null)
            throw new Error('PhaseTiming phase closed without an open scope.');
        const frame = scope.frames.pop();
        if (frame === undefined)
            throw new Error('PhaseTiming phase closed without an open phase.');
        const elapsed = PhaseTiming.getMonotonicNanoseconds() - frame.startNanos;
        const self = elapsed - frame.childNanos;
        const phaseKey = PhaseTiming.key(scope.lane, scope.engine, scope.stage) + '.' + PhaseTiming.phaseName(frame.phase);
        PhaseTiming.add(this._nanos, phaseKey, Number(self));
        PhaseTiming.add(this._counts, phaseKey, 1);
        scope.phaseNanos += self;
        if (scope.frames.length !== 0)
            scope.frames[scope.frames.length - 1].childNanos += elapsed;
    }

    private currentScope(): TimingScope | null {
        return this._scopes.length === 0 ? null : this._scopes[this._scopes.length - 1];
    }

    private static key(lane: string, engine: string, stage: TimingStage): string {
        return `${lane}.${engine}.${PhaseTiming.stageName(stage)}`;
    }

    private static add(totals: Map<string, number>, key: string, value: number): void {
        totals.set(key, (totals.get(key) ?? 0) + value);
    }
}
